import { getPlacementDataFromStorage } from '@features/placements/pages/create/helpers';
import type { TPlacement } from '@shared/types/placement';
import { PLACEMENT_HOUSING_RENT_TYPE } from '@shared/types/placement';
import { CURRENCY } from '@src/shared/types/currency';
import { string, array, mixed, object, number, boolean } from 'yup';

export const housingFormNames = {
  includeHousing: 'thirdStep.includeHousing',
  housingName: 'thirdStep.housingName',
  housingDescription: 'thirdStep.housingDescription',
  housingRent: 'thirdStep.housingRent',
  rentCurrency: 'thirdStep.rentCurrency',
  rentPeriod: 'thirdStep.rentPeriod',
  deposit: 'thirdStep.deposit',
  depositCurrency: 'thirdStep.depositCurrency',
  personsPerRoom: 'thirdStep.personsPerRoom',
  numberOfBathrooms: 'thirdStep.numberOfBathrooms',
  includeUtilities: 'thirdStep.includeUtilities',
  housingCountry: 'thirdStep.housingCountry',
  housingCity: 'thirdStep.housingCity',
  housingAddress1: 'thirdStep.housingAddress1',
  housingAddress2: 'thirdStep.housingAddress2',
  stateProvince: 'thirdStep.stateProvince',
  zipCode: 'thirdStep.zipCode',
  housingImages: 'thirdStep.housingImages',
} as const;

const existingPlacementData = (placementData?: TPlacement) => {
  if (!placementData) return undefined;

  return {
    includeHousing: !!placementData?.include_housing,
    housingName: placementData?.housing_name,
    housingDescription: placementData?.housing_description,
    housingRent: placementData?.housing_rent_fee,
    rentCurrency: placementData?.housing_rent_fee_currency,
    rentPeriod: placementData?.housing_rent_type,
    deposit: placementData?.housing_rent_deposite_fee,
    depositCurrency: placementData?.housing_rent_deposite_fee_currency,
    personsPerRoom: placementData?.housing_persons_per_room,
    numberOfBathrooms: placementData?.housing_bathrooms,
    includeUtilities: placementData?.include_utilities,
    housingAddress1: placementData?.housing_address_line_1,
    housingAddress2: placementData?.housing_address_line_2,
    housingCity: placementData?.housing_city,
    stateProvince: placementData?.housing_state,
    housingCountry: placementData?.housing_country?.code,
    zipCode: placementData?.housing_postal_code,
    housingImages: placementData?.housing_images,
  };
};

export const createThirdStepInitialValues = (placementData?: TPlacement) => {
  const cachedPlacementData = getPlacementDataFromStorage() as {
    thirdStep: ReturnType<typeof existingPlacementData>;
  };
  return {
    includeHousing: false,
    housingName: '',
    housingDescription: '',
    housingRent: '',
    rentCurrency: CURRENCY.usd,
    rentPeriod:
      PLACEMENT_HOUSING_RENT_TYPE.weekly as TPlacement['housing_rent_type'],
    deposit: '',
    depositCurrency: CURRENCY.usd,
    personsPerRoom: 0,
    numberOfBathrooms: 0,
    includeUtilities: false,
    housingAddress1: '',
    housingAddress2: '',
    housingCity: '',
    stateProvince: '',
    housingCountry: '',
    zipCode: '',
    housingImages: [] as TPlacement['housing_images'],

    ...cachedPlacementData?.thirdStep,
    ...existingPlacementData(placementData),
  };
};

export const housingFormSchema = object().shape({
  thirdStep: object().shape({
    includeHousing: boolean(),

    housingName: string()
      .nullable()
      .when('includeHousing', {
        is: (includeHousing: boolean) => includeHousing,
        then: () => string().required('Housing name is required'),
      }),
    housingDescription: string()
      .nullable()
      .when('includeHousing', {
        is: (includeHousing: boolean) => includeHousing,
        then: () =>
          string()
            .max(2000, 'Description cannot exceed 2000 characters')
            .required('Description is required'),
      }),
    housingRent: number()
      .nullable()
      .when('includeHousing', {
        is: (includeHousing: boolean) => includeHousing,
        then: () =>
          number().positive('Rent must be a positive number').nullable(),
      }),
    rentCurrency: string()
      .nullable()
      .when(['housingRent'], {
        is: (housingRent: number | null) => !!housingRent,
        then: () =>
          string().required(
            'Rent currency is required when rent amount is specified'
          ),
        otherwise: () => string().nullable(),
      }),
    rentPeriod: string()
      .nullable()
      .when('includeHousing', {
        is: (includeHousing: boolean) => includeHousing,
        then: () =>
          string().oneOf(
            Object.values(PLACEMENT_HOUSING_RENT_TYPE),
            'Invalid period'
          ),
      }),
    deposit: number()
      .nullable()
      .when('includeHousing', {
        is: (includeHousing: boolean) => includeHousing,
        then: () =>
          number().positive('Deposit must be a positive number').nullable(),
      }),
    depositCurrency: string()
      .nullable()
      .when(['deposit'], {
        is: (deposit: number | null) => !!deposit,
        then: () =>
          string().required(
            'Deposit currency is required when deposit amount is specified'
          ),
        otherwise: () => string().nullable(),
      }),
    personsPerRoom: number()
      .nullable()
      .when('includeHousing', {
        is: (includeHousing: boolean) => includeHousing,
        then: () =>
          number()
            .positive('Must be a positive number')
            .integer('Must be a whole number')
            .nullable(),
      }),
    numberOfBathrooms: number()
      .nullable()
      .when('includeHousing', {
        is: (includeHousing: boolean) => includeHousing,
        then: () =>
          number()
            .positive('Must be a positive number')
            .integer('Must be a whole number')
            .nullable(),
      }),
    includeUtilities: boolean(),
    housingAddress1: string()
      .nullable()
      .when('includeHousing', {
        is: (includeHousing: boolean) => includeHousing,
        then: () => string().required('Address is required'),
      }),
    housingAddress2: string().nullable(),
    housingCity: string()
      .nullable()
      .when('includeHousing', {
        is: (includeHousing: boolean) => includeHousing,
        then: () => string().required('City is required'),
      }),
    stateProvince: string().nullable(),
    housingCountry: string()
      .nullable()
      .when('includeHousing', {
        is: (includeHousing: boolean) => includeHousing,
        then: () => string().required('Country is required'),
      }),
    zipCode: string().nullable(),
    housingImages: array()
      .nullable()
      .of(mixed())
      .when('includeHousing', {
        is: (includeHousing: boolean) => includeHousing,
        then: () => array().of(mixed()),
      }),
  }),
});

export type THousingFormValues = ReturnType<
  typeof createThirdStepInitialValues
>;
